import { useState, type ReactNode } from "react";
import { yerelGunBasi } from "@koclukpanel/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  DURUM_SINIFLARI,
  DurumRozeti,
  FiltreKarti,
  Ikon,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  Secim,
  Sekmeler,
  Tablo,
  cn,
  type IkonAdi,
  type UnkDurum,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import { izinKapsami } from "./izin";
import {
  ApiHatasi,
  benGetir,
  duyuruOkumalari,
  duyuruOkundu,
  duyuruOlustur,
  duyurulariGetir,
  gorevAta,
  gorevDurumuDegistir,
  gorevlerimiGetir,
  izinKararaBagla,
  izinTalebiAc,
  izinleriGetir,
  personelListesi,
  type Ben,
  type DuyuruKime,
  type Gorev,
  type GorevDurumu,
  type HedefKitle,
  type IzinDurumu,
} from "./api";

/**
 * OPERASYON — duyuru, gorev, izin (matris §5.8)
 */

const GOREV_ADI: Record<GorevDurumu, string> = {
  PENDING_ACCEPT: "Kabul bekliyor",
  IN_PROGRESS: "Devam ediyor",
  COMPLETED: "Tamamlandı",
  FAILED: "Yapılamadı",
};

/* UNİKOÇ dört durum rengi (tarama-1 §3.6): kabul bekleyen turuncu, işlemde mavi, tamam yeşil, yapılamadı kırmızı. */
const GOREV_RENGI: Record<GorevDurumu, UnkDurum> = {
  PENDING_ACCEPT: "bekliyor",
  IN_PROGRESS: "planli",
  COMPLETED: "tamamlandi",
  FAILED: "eksik",
};

const IZIN_ADI: Record<IzinDurumu, string> = {
  PENDING: "Bekliyor",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
  CANCELLED: "İptal",
};

const KITLE_ADI: Record<HedefKitle, string> = {
  ALL_STUDENTS: "Tüm öğrenciler",
  ALL_PARENTS: "Tüm veliler",
  ALL_STAFF: "Tüm personel",
  BY_GRADE: "Sınıfa göre",
  BY_FIELD: "Alana göre",
  BY_COACH: "Uzmana göre",
  CUSTOM: "Seçili kişiler",
};

/** UNİKOÇ tablo tarihi: 16.12.2025. */
function kisaTarih(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function tarih(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function Operasyon() {
  /*
    Varsayılan GÖREVLER: menü adı "Görevlerim" ve UNİKOÇ'ta bu ekran doğrudan "Görev Listesi"
    ile açılıyor (sürüm 9a, iç düzen turu L1). Duyurular ve izinler ayrı sekmede kalıyor.
  */
  const [gorunum, setGorunum] = useState<"duyurular" | "gorevler" | "izinler">("gorevler");

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama="Size atanan görevleri görüntüleyin, durumunu güncelleyin; duyurular ve izinler ayrı sekmede." />
      {/*
        SEKME DEĞİL, SÜZGEÇ DÜĞMELERİ — fark ekran okuyucuda.

        Elle yazılmış `role="tab"` şeridi vardı: `aria-selected` koyulmuş
        ama ok tuşlarıyla gezinme, gezinen odak ve `tabpanel` bağı YOKTU.
        Eksik kurulmuş bir ARIA deseni hiç yazmamaktan KÖTÜ — kullanıcıya
        "burada ok tuşlarıyla gezebilirsin" diye söz verip tutmuyor.

        `Sekmeler` `aria-pressed` kullanıyor: aynı listeyi süzen
        basılı-kalan düğmeler, ki gerçekte olan bu.
      */}
      <Sekmeler
        etiket="Operasyon görünümü"
        gorunum="hap"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={[
          { deger: "gorevler", etiket: "Görevlerim" },
          { deger: "duyurular", etiket: "Duyurular" },
          { deger: "izinler", etiket: "İzinler" },
        ]}
      />

      {gorunum === "duyurular" && <Duyurular />}
      {gorunum === "gorevler" && <Gorevler />}
      {gorunum === "izinler" && <Izinler />}
    </div>
  );
}

// ---------------------------------------------------------------
// DUYURULAR
// ---------------------------------------------------------------

/**
 * ================================================================
 * EKRAN HEDEF KITLE SUZMESI YAPMIYOR
 * ================================================================
 * Sunucu zaten yalnizca BU KULLANICIYA acik duyurulari donduruyor
 * ve o eleme birim testi yazilmis saf bir fonksiyonda yapiliyor.
 *
 * Ekranda ikinci bir suzme yazsaydik iki dogruluk kaynagi olurdu:
 * biri fazla gosterir, digeri az gosterirdi ve hangisinin dogru
 * oldugu belirsizlesirdi. Burada gelen liste OLDUGU GIBI ciziliyor.
 */
function Duyurular() {
  const istemci = useQueryClient();
  const [formAcik, setFormAcik] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["duyurular"],
    queryFn: duyurulariGetir,
  });

  /*
    F-NTF-08b — YAZMA KAPSAMI soruluyor, varlık değil. ALL yönetici
    formunu, TEAM/ASSIGNED "kendi öğrencilerim" formunu görür; izni
    olmayan "Duyuru yaz" düğmesini hiç görmez (önceden görüp 403
    alıyordu). Kural sunucuda (`duyuruHedefiniCoz`); bu yalnız kolaylık.
  */
  const ben = useQuery<Ben>({ queryKey: ["ben"], queryFn: benGetir, retry: false });
  const yazmaKapsami =
    ben.data !== undefined ? izinKapsami(ben.data.izinler, "operasyon.duyuru.yonet") : null;
  const yazabilir =
    yazmaKapsami === "ALL" || yazmaKapsami === "TEAM" || yazmaKapsami === "ASSIGNED";

  const okuMut = useMutation({
    // API fonksiyonu DOGRUDAN verilmiyor: React Query `mutationFn`e
    // ikinci argüman olarak kendi bağlamını (`client`, `meta`…)
    // geçiriyor ve o nesne API katmanına sızıyordu. Şu an zararsız
    // ama uç bir gün ikinci parametre alırsa sessizce yanlış çağrılır.
    mutationFn: (id: string) => duyuruOkundu(id),
    onSuccess: async () => {
      await istemci.invalidateQueries({ queryKey: ["duyurular"] });
    },
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Duyurular alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {yazabilir && (
        <div>
          <Buton onClick={() => setFormAcik((a) => !a)} aria-expanded={formAcik}>
            {formAcik ? "Formu kapat" : "Duyuru yaz"}
          </Buton>
        </div>
      )}

      {formAcik && yazabilir && (
        <DuyuruFormu
          kapsam={yazmaKapsami === "ALL" ? "ALL" : "KENDI"}
          ekip={yazmaKapsami === "TEAM"}
          onKapat={() => setFormAcik(false)}
        />
      )}

      {data.duyurular.length === 0 ? (
        <BosDurum baslik="Duyuru yok" aciklama="Size açık bir duyuru bulunmuyor." />
      ) : (
        <ul className="flex flex-col gap-3">
          {data.duyurular.map((d) => (
            <Kart olarak="li" key={d.id} vurgu={d.okunduMu ? "yok" : "marka"}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-notr-900 text-sm font-semibold">
                    {d.isPinned && <span aria-label="Sabitlenmiş">📌 </span>}
                    {d.title}
                  </h3>
                  <p className="text-notr-700 mt-1 text-sm whitespace-pre-wrap">{d.body}</p>
                  <p className="text-notr-600 mt-2 text-xs">{tarih(d.publishAt)}</p>

                  {/* Dar kapsamda okunma listesi yalnız yazana açık (F-NTF-08b). */}
                  {(yazmaKapsami === "ALL" || (yazabilir && d.benYazdim === true)) && (
                    <OkumaDurumu duyuruId={d.id} />
                  )}
                </div>

                {!d.okunduMu && (
                  <Buton
                    gorunum="ikincil"
                    onClick={() => okuMut.mutate(d.id)}
                    disabled={okuMut.isPending}
                  >
                    Okudum
                  </Buton>
                )}
              </div>

              {d.linkUrl && (
                <a
                  href={d.linkUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-marka-600 focus-visible:outline-marka-600 mt-2 inline-block text-sm underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  Ayrıntı
                </a>
              )}
            </Kart>
          ))}
        </ul>
      )}
    </div>
  );
}

const KIME_ADI: Record<DuyuruKime, string> = {
  IKISI: "Öğrenciler ve velileri",
  OGRENCI: "Yalnız öğrenciler",
  VELI: "Yalnız veliler",
};

/**
 * `kapsam="KENDI"` — F-NTF-08b koç/ekip duyurusu. Hedef kitle seçilmiyor:
 * sunucu `BY_COACH` hedefini kapsamdan kendisi kuruyor (koç için kendisi,
 * ekip lideri için ekibi). Formun sorduğu tek şey KİME: öğrenci, veli
 * ya da ikisi.
 */
function DuyuruFormu({
  kapsam,
  ekip,
  onKapat,
}: {
  kapsam: "ALL" | "KENDI";
  ekip: boolean;
  onKapat: () => void;
}) {
  const istemci = useQueryClient();
  const [baslik, setBaslik] = useState("");
  const [govde, setGovde] = useState("");
  const [kitle, setKitle] = useState<HedefKitle>("ALL_STUDENTS");
  const [kime, setKime] = useState<DuyuruKime>("IKISI");

  const mut = useMutation({
    mutationFn: () =>
      kapsam === "ALL"
        ? duyuruOlustur({ baslik: baslik.trim(), govde: govde.trim(), hedefKitle: kitle })
        : duyuruOlustur({
            baslik: baslik.trim(),
            govde: govde.trim(),
            hedefKitle: "BY_COACH",
            kime,
          }),
    onSuccess: async () => {
      setBaslik("");
      setGovde("");
      onKapat();
      await istemci.invalidateQueries({ queryKey: ["duyurular"] });
    },
  });

  // Hedefli kitleler filtre alani ister; o alanlar henuz yazilmadi.
  // Secilmesine izin verip BOS filtre gondermek, duyurunun kimseye
  // gitmemesine yol acardi — sunucu bos filtreyi kimseye acmiyor.
  const filtreGerekiyor =
    kapsam === "ALL" && ["BY_GRADE", "BY_FIELD", "BY_COACH", "CUSTOM"].includes(kitle);

  return (
    <form
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!filtreGerekiyor && baslik.trim().length >= 3 && govde.trim()) mut.mutate();
      }}
    >
      <h3 className="text-notr-900 text-sm font-semibold">Yeni duyuru</h3>

      <Alan etiket="Başlık" value={baslik} onChange={(e) => setBaslik(e.target.value)} required />

      <MetinAlani
        etiket="Metin"
        value={govde}
        onChange={(e) => setGovde(e.target.value)}
        rows={4}
      />

      {kapsam === "ALL" ? (
        <Secim etiket="Kime" value={kitle} onChange={(e) => setKitle(e.target.value as HedefKitle)}>
          {(Object.keys(KITLE_ADI) as HedefKitle[]).map((k) => (
            <option key={k} value={k}>
              {KITLE_ADI[k]}
            </option>
          ))}
        </Secim>
      ) : (
        <Secim
          etiket="Kime"
          value={kime}
          onChange={(e) => setKime(e.target.value as DuyuruKime)}
          yardim={
            ekip
              ? "Ekibinizdeki uzmanların öğrencilerine ve velilerine gider."
              : "Yalnız size atanmış öğrencilere ve onların velilerine gider."
          }
        >
          {(Object.keys(KIME_ADI) as DuyuruKime[]).map((k) => (
            <option key={k} value={k}>
              {KIME_ADI[k]}
            </option>
          ))}
        </Secim>
      )}

      {filtreGerekiyor && (
        <p role="alert" className="rounded-alan bg-uyari-500/10 text-uyari-600 p-3 text-sm">
          Bu hedef kitle bir seçim listesi gerektiriyor (sınıf, alan, uzman ya da kişi) ve o ekran
          henüz yazılmadı. Boş filtreyle gönderilen duyuru <strong>kimseye</strong> ulaşmaz; bu
          yüzden gönderim kapalı.
        </p>
      )}

      <div className="flex gap-2">
        <Buton
          type="submit"
          disabled={filtreGerekiyor || baslik.trim().length < 3 || !govde.trim() || mut.isPending}
        >
          {mut.isPending ? "Yayınlanıyor…" : "Yayınla"}
        </Buton>
        <Buton gorunum="sessiz" type="button" onClick={onKapat}>
          Kapat
        </Buton>
      </div>

      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Duyuru yayınlanamadı."}
        </p>
      )}
    </form>
  );
}

// ---------------------------------------------------------------
// GOREVLER
// ---------------------------------------------------------------

function Gorevler() {
  const istemci = useQueryClient();
  const [acikId, setAcikId] = useState<string | null>(null);
  /*
    UNİKOÇ "Görev Listesi" süzgeci: arama, durum, bitiş tarihi aralığı. Süzme EKRANDA, gelen
    listenin üstünde — yeni uç ya da sorgu yok; sunucunun döndürdüğü kişisel görev listesi
    olduğu gibi kalıyor, yalnız gösterilen satırlar daralıyor. Sayaçlar SÜZGEÇTEN ÖNCE sayılıyor:
    "kaç görevim kabul bekliyor" sorusunun cevabı aramaya göre değişmemeli.
  */
  const [ara, setAra] = useState("");
  const [durumSuz, setDurumSuz] = useState<GorevDurumu | "">("");
  const [bas, setBas] = useState("");
  const [bit, setBit] = useState("");

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["gorevlerim"],
    queryFn: () => gorevlerimiGetir(),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Görevler alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const yenile = (): void => {
    void istemci.invalidateQueries({ queryKey: ["gorevlerim"] });
  };

  // Açık görev listeden düştüyse (ör. başka yerde güncellendi) detay kendiliğinden kapanır.
  const acikGorev = acikId ? data.gorevler.find((g) => g.id === acikId) : undefined;
  if (acikGorev) return <GorevDetayi gorev={acikGorev} onGeri={() => setAcikId(null)} />;

  if (data.gorevler.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {/*
          ATAMA FORMU BOS DURUMDA DA VAR. Yalnizca liste doluyken
          gosterseydik, hic gorevi olmayan yonetici "gorev atanacak yer"
          bulamazdi — tam da atama yapmasi gereken kisi.
        */}
        <GorevAtama onAtandi={yenile} />
        <BosDurum baslik="Görev yok" aciklama="Size atanmış açık görev bulunmuyor." />
      </div>
    );
  }

  const sayi = (d: GorevDurumu): number => data.gorevler.filter((g) => g.status === d).length;
  const arananMetin = ara.trim().toLocaleLowerCase("tr-TR");
  const gorunen = data.gorevler.filter((g) => {
    if (durumSuz && g.status !== durumSuz) return false;
    if (arananMetin && !g.title.toLocaleLowerCase("tr-TR").includes(arananMetin)) return false;
    // Tarih süzgeci BİTİŞ tarihine bakıyor; bitişi olmayan görev aralık seçilince düşer.
    const gun = g.dueDate ? g.dueDate.slice(0, 10) : null;
    if ((bas || bit) && gun === null) return false;
    if (bas && gun !== null && gun < bas) return false;
    if (bit && gun !== null && gun > bit) return false;
    return true;
  });
  const suzgecVar = Boolean(ara || durumSuz || bas || bit);

  return (
    <div className="flex flex-col gap-4">
      <GorevAtama onAtandi={yenile} />

      {/* UNİKOÇ "Görev Listesi" kartı: süzgeç satırı + Temizle + dört durum sayacı aynı kartta. */}
      <FiltreKarti baslik="Görev Listesi" ikon="gorevler">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,0.8fr)] md:items-end">
          <Alan
            etiket="Görev ara"
            type="search"
            placeholder="Görev adı ara"
            value={ara}
            onChange={(e) => setAra(e.target.value)}
          />
          <Secim
            etiket="Durum"
            value={durumSuz}
            onChange={(e) => setDurumSuz(e.target.value as GorevDurumu | "")}
          >
            <option value="">Hepsi</option>
            {(Object.keys(GOREV_ADI) as GorevDurumu[]).map((d) => (
              <option key={d} value={d}>
                {GOREV_ADI[d]}
              </option>
            ))}
          </Secim>
          <Alan
            etiket="Bitiş (başlangıç)"
            type="date"
            value={bas}
            onChange={(e) => setBas(e.target.value)}
          />
          <Alan
            etiket="Bitiş (son)"
            type="date"
            value={bit}
            onChange={(e) => setBit(e.target.value)}
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Buton
            disabled={!suzgecVar}
            onClick={() => {
              setAra("");
              setDurumSuz("");
              setBas("");
              setBit("");
            }}
          >
            Temizle
          </Buton>
        </div>
        {/*
          UNİKOÇ durum kartları (tarama-1 §3.6): etiket üstte gri küçük, sayı altta kalın ve
          durumun renginde. Sayılar listeden türetiliyor — yeni uç yok. Renk tek sinyal değil:
          etiket her kartta yazılı.
        */}
        <dl className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <GorevSayisi etiket="Kabul Bekleyen" sayi={sayi("PENDING_ACCEPT")} durum="bekliyor" />
          <GorevSayisi etiket="İşlemde" sayi={sayi("IN_PROGRESS")} durum="planli" />
          <GorevSayisi etiket="Tamamlandı" sayi={sayi("COMPLETED")} durum="tamamlandi" />
          <GorevSayisi etiket="Tamamlanamadı" sayi={sayi("FAILED")} durum="eksik" />
        </dl>
      </FiltreKarti>

      {gorunen.length === 0 ? (
        <BosDurum
          baslik="Süzgece uyan görev yok"
          aciklama="Aramayı ya da tarih aralığını temizleyin."
        />
      ) : (
        <Kart gorunum="unk" olarak="div" className="p-2 md:p-4">
          <Tablo
            baslikTuru="degrade"
            satirlar={gorunen}
            anahtar={(g) => g.id}
            sutunlar={[
              {
                baslik: "Görev Adı",
                mobil: true,
                hucre: (g) => (
                  <span className="text-metin-koyu flex flex-wrap items-center gap-1.5 font-semibold">
                    {g.title}
                    {g.priority === 1 && <Rozet ton="uyari">Yüksek</Rozet>}
                  </span>
                ),
              },
              {
                baslik: "Durum",
                mobil: true,
                hucre: (g) => (
                  <DurumRozeti durum={GOREV_RENGI[g.status]}>{GOREV_ADI[g.status]}</DurumRozeti>
                ),
              },
              {
                baslik: "Bitiş Tarihi",
                hucre: (g) => (g.dueDate ? kisaTarih(g.dueDate) : "—"),
              },
              {
                baslik: "İşlemler",
                mobil: true,
                etiketGizli: true,
                hucre: (g) => (
                  <button
                    type="button"
                    onClick={() => setAcikId(g.id)}
                    aria-label={`${g.title} görevinin detayı`}
                    className={cn(
                      "min-h-dokunma text-planli-metin ring-planli-nokta inline-flex items-center gap-1.5 rounded-lg px-3 text-sm font-medium ring-1 ring-inset",
                      "hover:bg-planli-zemin ease-cikis transition-colors duration-(--sure-anlik)",
                      "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                    )}
                  >
                    <Ikon ad="gorunum" className="size-4" />
                    Detay
                  </button>
                ),
              },
            ]}
          />
          <p className="text-metin-ikincil px-2 pt-3 text-sm">
            {data.gorevler.length} kayıttan {gorunen.length} kayıt gösteriliyor
          </p>
        </Kart>
      )}
    </div>
  );
}

/**
 * GÖREV DETAYI — UNİKOÇ "Görev Detayları" (Gorevler-Details): başlık, bitiş tarihi, açıklama,
 * durum ve "Durumu Güncelle". Durum düğmeleri SUNUCUDAN gelen `izinliGecisler`den üretiliyor;
 * tamamlanmış görev yeniden açılamıyor — sunucu o geçişi hiç göndermiyor, ekran da düğme çizmiyor.
 */
function GorevDetayi({ gorev: g, onGeri }: { gorev: Gorev; onGeri: () => void }) {
  const istemci = useQueryClient();
  const [sebep, setSebep] = useState("");

  const mut = useMutation({
    mutationFn: (v: { hedef: GorevDurumu; sebep?: string }) =>
      gorevDurumuDegistir(g.id, v.hedef, v.sebep),
    onSuccess: async () => {
      setSebep("");
      await istemci.invalidateQueries({ queryKey: ["gorevlerim"] });
    },
  });

  return (
    <Kart gorunum="unk" olarak="div" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <span
          aria-hidden="true"
          className="bg-mor-500 text-notr-0 grid size-8 place-items-center rounded-full"
        >
          <Ikon ad="bilgi" className="size-4" />
        </span>
        <h2 className="text-metin-koyu mt-2 text-2xl font-bold">Görev Detayları</h2>
        <p className="text-metin-ikincil text-sm">
          Görev bilgilerini görüntüleyin; yetkiniz varsa durumunu güncelleyebilirsiniz.
        </p>
      </div>

      <h3 className="text-metin-koyu flex flex-wrap items-center gap-2 text-lg font-bold">
        <Ikon ad="gorevler" className="size-5" />
        {g.title}
        {g.priority === 1 && <Rozet ton="uyari">Yüksek</Rozet>}
      </h3>

      <div className="grid gap-3 md:grid-cols-2">
        <DetayKutusu ikon="takvim" baslik="Bitiş Tarihi">
          {g.dueDate ? kisaTarih(g.dueDate) : "—"}
        </DetayKutusu>
        <DetayKutusu ikon="bekliyor" baslik="Atanma Tarihi">
          {kisaTarih(g.createdAt)}
        </DetayKutusu>
      </div>

      <DetayKutusu ikon="sohbet" baslik="Açıklama">
        {g.description ? (
          <span className="whitespace-pre-line">{g.description}</span>
        ) : (
          <span className="text-metin-ikincil">Açıklama yazılmamış.</span>
        )}
      </DetayKutusu>

      <DetayKutusu ikon="gorevler" baslik="Durum">
        <DurumRozeti durum={GOREV_RENGI[g.status]}>{GOREV_ADI[g.status]}</DurumRozeti>
      </DetayKutusu>

      {g.izinliGecisler.length > 0 && (
        <div className="border-mor-kenar flex flex-col gap-3 rounded-2xl border p-4 md:max-w-xl">
          <p className="text-metin-koyu flex items-center gap-2 text-sm font-bold">
            <Ikon ad="duzenle" className="size-4" />
            Durumu Güncelle
          </p>
          <div className="flex flex-wrap gap-2">
            {g.izinliGecisler.map((h) => (
              <Buton
                key={h}
                gorunum={h === "FAILED" ? "sessiz" : "birincil"}
                // "Yapilamadi" sebepsiz gonderilmiyor: sunucu da reddediyor ama kullaniciyi
                // once burada uyarmak bos bir istek atmaktan iyi.
                disabled={mut.isPending || (h === "FAILED" && sebep.trim().length < 5)}
                onClick={() =>
                  mut.mutate({ hedef: h, sebep: h === "FAILED" ? sebep.trim() : undefined })
                }
              >
                {GOREV_ADI[h]}
              </Buton>
            ))}
          </div>
          {g.izinliGecisler.includes("FAILED") && (
            <Alan
              etiket="Yapılamadı ise sebep"
              value={sebep}
              onChange={(e) => setSebep(e.target.value)}
              yardim="Sebepsiz bir başarısızlık ne tekrarlanmasını engeller ne de kimin neye ihtiyacı olduğunu gösterir."
            />
          )}
        </div>
      )}

      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Görev güncellenemedi."}
        </p>
      )}

      <div className="flex justify-end">
        <Buton gorunum="ikincil" onClick={onGeri}>
          ← Listeye Dön
        </Buton>
      </div>
    </Kart>
  );
}

function DetayKutusu({
  ikon,
  baslik,
  children,
}: {
  ikon: IkonAdi;
  baslik: string;
  children: ReactNode;
}) {
  return (
    <div className="border-mor-kenar bg-notr-0 flex flex-col gap-1.5 rounded-2xl border p-4 text-sm">
      <p className="text-metin-koyu flex items-center gap-2 font-bold">
        <Ikon ad={ikon} className="size-4" />
        {baslik}
      </p>
      <div className="text-metin-koyu">{children}</div>
    </div>
  );
}

/** Durum kartı — etiket üstte gri, sayı altta büyük ve durum renginde (UNİKOÇ Görev Listesi). */
function GorevSayisi({ etiket, sayi, durum }: { etiket: string; sayi: number; durum: UnkDurum }) {
  return (
    <div className="bg-notr-0 border-mor-kenar rounded-2xl border px-4 py-3">
      <dt className="text-metin-ikincil text-xs font-medium">{etiket}</dt>
      <dd className={cn("text-2xl font-bold tabular-nums", DURUM_SINIFLARI[durum].metin)}>
        {sayi}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------
// IZINLER
// ---------------------------------------------------------------

function Izinler() {
  const istemci = useQueryClient();
  const [formAcik, setFormAcik] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["izinler"],
    queryFn: () => izinleriGetir(),
  });

  const kararMut = useMutation({
    mutationFn: (k: { id: string; hedef: "APPROVED" | "REJECTED" }) =>
      izinKararaBagla(k.id, k.hedef),
    onSuccess: async () => {
      await istemci.invalidateQueries({ queryKey: ["izinler"] });
    },
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="İzinler alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Buton onClick={() => setFormAcik((a) => !a)} aria-expanded={formAcik}>
          {formAcik ? "Formu kapat" : "İzin talebi aç"}
        </Buton>
      </div>

      {formAcik && <IzinFormu onKapat={() => setFormAcik(false)} />}

      {data.izinler.length === 0 ? (
        <BosDurum baslik="İzin kaydı yok" />
      ) : (
        <ul className="flex flex-col gap-2">
          {data.izinler.map((i) => (
            <Kart key={i.id} olarak="li" className="flex flex-wrap items-center gap-3">
              <span className="min-w-0 flex-1">
                <span className="text-notr-900 block text-sm font-medium">
                  {tarih(i.startDate)} – {tarih(i.endDate)}
                </span>
                <span className="text-notr-600 block text-sm">{i.reason}</span>
              </span>

              <Rozet className="shrink-0">{IZIN_ADI[i.status]}</Rozet>

              {/*
                Karar dugmeleri YALNIZCA bekleyen taleplerde. Kendi
                talebini onaylama denemesi sunucuda 403 `KENDI_IZNI`
                doner; ekran o dugmeyi gizlemiyor cunku "bu talep
                benim mi" bilgisi listede yok — kullaniciya yanlis
                bir dugme gostermektense sunucunun net mesajini
                gostermek daha dogru.
              */}
              {i.status === "PENDING" && (
                <span className="flex shrink-0 gap-2">
                  <Buton
                    onClick={() => kararMut.mutate({ id: i.id, hedef: "APPROVED" })}
                    disabled={kararMut.isPending}
                  >
                    Onayla
                  </Buton>
                  <Buton
                    gorunum="sessiz"
                    onClick={() => kararMut.mutate({ id: i.id, hedef: "REJECTED" })}
                    disabled={kararMut.isPending}
                  >
                    Reddet
                  </Buton>
                </span>
              )}
            </Kart>
          ))}
        </ul>
      )}

      {kararMut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {kararMut.error instanceof ApiHatasi
            ? kararMut.error.message
            : "İzin karara bağlanamadı."}
        </p>
      )}
    </div>
  );
}

function IzinFormu({ onKapat }: { onKapat: () => void }) {
  const istemci = useQueryClient();
  const [baslangic, setBaslangic] = useState("");
  const [bitis, setBitis] = useState("");
  const [gerekce, setGerekce] = useState("");
  const [devirNotu, setDevirNotu] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      izinTalebiAc({
        baslangic,
        bitis,
        gerekce: gerekce.trim(),
        devirNotu: devirNotu.trim() || null,
      }),
    onSuccess: async () => {
      onKapat();
      await istemci.invalidateQueries({ queryKey: ["izinler"] });
    },
  });

  // Bugunden onceki tarih secilemiyor: izin bir PLANLAMA araci ve
  // ogrenci devri once ayarlanmali. Sunucu da reddediyor.
  const bugun = yerelGunBasi().toISOString().slice(0, 10);

  return (
    <form
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (baslangic && bitis && gerekce.trim().length >= 3) mut.mutate();
      }}
    >
      <h3 className="text-notr-900 text-sm font-semibold">İzin talebi</h3>

      <div className="flex flex-wrap gap-3">
        <Alan
          etiket="Başlangıç"
          type="date"
          min={bugun}
          value={baslangic}
          onChange={(e) => setBaslangic(e.target.value)}
          required
        />
        <Alan
          etiket="Bitiş"
          type="date"
          min={baslangic || bugun}
          value={bitis}
          onChange={(e) => setBitis(e.target.value)}
          required
        />
      </div>

      <Alan
        etiket="Gerekçe"
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        required
      />
      <Alan
        etiket="Devir notu"
        value={devirNotu}
        onChange={(e) => setDevirNotu(e.target.value)}
        yardim="İzin süresince öğrencilerinize kimin bakacağı ve nelere dikkat etmesi gerektiği."
      />

      <div className="flex gap-2">
        <Buton
          type="submit"
          disabled={!baslangic || !bitis || gerekce.trim().length < 3 || mut.isPending}
        >
          {mut.isPending ? "Gönderiliyor…" : "Talebi gönder"}
        </Buton>
        <Buton gorunum="sessiz" type="button" onClick={onKapat}>
          Kapat
        </Buton>
      </div>

      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "İzin talebi açılamadı."}
        </p>
      )}
    </form>
  );
}

/**
 * PERSONELE GÖREV ATAMA — matris §5.8
 *
 * ================================================================
 * "GÖREVLERİM" SEKMESİNİN İÇİNDE, AYRI SEKME DEĞİL
 * ================================================================
 * Görev ATAMAK ve kendi görevini GÖRMEK aynı ekranda: atayan kişi
 * atadığı görevin listeye düştüğünü görmeli. Ayrı sekme olsaydı
 * "gerçekten atandı mı" sorusu için sekme değiştirmek gerekirdi.
 *
 * Form kapalı başlıyor. Açık başlasaydı, günlük işi kendi görevlerine
 * bakmak olan kişi her sekme açılışında bir formla karşılaşırdı.
 */
function GorevAtama({ onAtandi }: { onAtandi: () => void }) {
  const [acik, setAcik] = useState(false);
  const [atananId, setAtananId] = useState("");
  const [baslik, setBaslik] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [sonTarih, setSonTarih] = useState("");
  const [oncelik, setOncelik] = useState("2");
  const [bilgi, setBilgi] = useState<string | null>(null);

  const personel = useQuery({
    queryKey: ["personel-listesi"],
    queryFn: () => personelListesi(),
    retry: false,
    // Form açılmadan sorgu ATILMIYOR: personel listesi ayrı bir izinde
    // ve görev atamayacak kişide her sekme açılışında 403 üretirdi.
    enabled: acik,
  });

  const ata = useMutation({
    mutationFn: () =>
      gorevAta({
        atananId,
        baslik: baslik.trim(),
        aciklama: aciklama.trim() || null,
        sonTarih: sonTarih || null,
        oncelik: Number(oncelik),
      }),
    onSuccess: () => {
      setBilgi("Görev atandı.");
      setBaslik("");
      setAciklama("");
      setSonTarih("");
      onAtandi();
    },
  });

  if (!acik) {
    return (
      <div>
        <Buton gorunum="ikincil" onClick={() => setAcik(true)}>
          Personele görev ata
        </Buton>
      </div>
    );
  }

  return (
    <Kart baslik="Personele görev ata">
      <div className="flex flex-col gap-3">
        {personel.isError ? (
          /*
            PERSONEL LİSTESİ AYRI BİR İZİNDE (`kisi.personel.goruntule`).
            Görev atayabilen ama personeli göremeyen bir rol tanımlanırsa
            boş bir açılır liste görünür ve "kimse yok" gibi okunurdu.
            Sunucunun cümlesi gösteriliyor.
          */
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {personel.error instanceof ApiHatasi
              ? personel.error.message
              : "Personel listesi alınamadı."}
          </p>
        ) : personel.isPending ? (
          <Iskelet satir={2} />
        ) : (
          <>
            <Secim etiket="Kime" value={atananId} onChange={(e) => setAtananId(e.target.value)}>
              <option value="">Seçin…</option>
              {/* Arşivlenmiş personel listede YOK: kapanmış bir hesaba görev
                  atamak, hiç yapılmayacak bir işi kuyruğa koymak olurdu. */}
              {personel.data.personel
                .filter((p) => !p.archivedAt)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.firstName} {p.lastName}
                  </option>
                ))}
            </Secim>

            <Alan
              etiket="Görev başlığı"
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
              yardim="Ne yapılacağı tek cümlede anlaşılmalı."
            />
            <MetinAlani
              etiket="Açıklama"
              rows={3}
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Alan
                etiket="Son tarih"
                type="date"
                value={sonTarih}
                onChange={(e) => setSonTarih(e.target.value)}
                yardim="İsteğe bağlı."
              />
              <Secim etiket="Öncelik" value={oncelik} onChange={(e) => setOncelik(e.target.value)}>
                <option value="1">Acil</option>
                <option value="2">Normal</option>
                <option value="3">Düşük</option>
              </Secim>
            </div>

            {ata.isError && (
              <p role="alert" className="text-hata-500 text-sm font-medium">
                {ata.error instanceof ApiHatasi ? ata.error.message : "Görev atanamadı."}
              </p>
            )}
            {bilgi && (
              <p role="status" className="text-notr-700 text-sm">
                {bilgi}
              </p>
            )}

            <div className="flex gap-2">
              <Buton
                yukleniyor={ata.isPending}
                disabled={!atananId || baslik.trim().length < 3}
                onClick={() => {
                  setBilgi(null);
                  ata.mutate();
                }}
              >
                Görevi ata
              </Buton>
              <Buton gorunum="sessiz" onClick={() => setAcik(false)}>
                Kapat
              </Buton>
            </div>
          </>
        )}
      </div>
    </Kart>
  );
}

/**
 * DUYURUYU KİMLER OKUDU — matris §5.8
 *
 * `GET /operasyon/duyurular/:id/okumalar` yazılıydı, hiçbir ekran
 * çağırmıyordu. Duyuru yazan kişi kimin okuduğunu göremiyordu; yani
 * "herkes gördü mü" sorusunun cevabı vardı, ulaşılamıyordu.
 *
 * Uç `operasyon.duyuru.yonet` istiyor. İzni olmayana hiçbir şey
 * gösterilmiyor — 403'ü bir hata gibi göstermek, olmayan bir arızayı
 * haber vermek olurdu.
 *
 * Ancak AÇILDIĞINDA isteniyor: listede on duyuru varsa on ayrı
 * sorgu atmak, kimsenin bakmadığı bir sayı için sunucuyu yormak olur.
 */
function OkumaDurumu({ duyuruId }: { duyuruId: string }) {
  const [acik, setAcik] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ["duyuru-okumalari", duyuruId],
    queryFn: () => duyuruOkumalari(duyuruId),
    enabled: acik,
    retry: false,
  });

  if (!acik) {
    return (
      <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
        Kimler okudu
      </Buton>
    );
  }

  if (isError) return null;

  return (
    <div className="rounded-alan bg-notr-50 mt-2 p-2 text-xs">
      {isPending ? (
        <span className="text-notr-600">Okunuyor…</span>
      ) : (
        <>
          <p className="text-notr-800 font-medium">{data.toplam} kişi okudu</p>
          {data.toplam === 0 && (
            <p className="text-notr-600 mt-0.5">
              Henüz kimse okumadı. Duyuru yayında ama görülmemiş olabilir.
            </p>
          )}
        </>
      )}
    </div>
  );
}
